import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '@shared/core';
import { IconButton, TextV2, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { CustomCard as EditIcon } from '@withjoy/joykit/icons';
import { ProgressBar } from '@shared/components/ProgressBar';
import { useRegistryGiftCounts } from '../../../RegistryGiftCounts/RegistryGiftCounts.controller';
import { StyledTotalProgressTextWrapper, StyledTotalGiftCountWrapper, styles } from '../../../RegistryGiftCounts/components/ProgressSummary/styles';

const AnimatedNumber = ({ value }: { value: number }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let animationFrame: number;
    let start: number | null = null;
    const duration = 800;

    const animate = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      setDisplayValue(Math.round(progress * value));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [value]);

  return <span>{displayValue}</span>;
};

interface ProgressSummaryProps
  extends Pick<
    ReturnType<typeof useRegistryGiftCounts>,
    'completionPercent' | 'formattedCompletionPercent' | 'handleOnEditClick' | 'subtitle' | 'currentGiftCount' | 'targetGiftCount'
  > {}

export const ProgressSummary = ({ completionPercent, formattedCompletionPercent, handleOnEditClick, subtitle, currentGiftCount, targetGiftCount }: ProgressSummaryProps) => {
  const { t } = useTranslation('sharedComponents');
  const registryTexts = t('registryGiftCounts');

  const getCompletionText = () => {
    if (completionPercent < 100) {
      return (
        <>
          <AnimatedNumber value={currentGiftCount} /> {registryTexts.of()} {targetGiftCount} {registryTexts.giftTotal()}
        </>
      );
    }
    return (
      <>
        <AnimatedNumber value={currentGiftCount} /> {registryTexts.giftsAdded()}
      </>
    );
  };

  const handleEditClick = useCallback(() => {
    handleOnEditClick();
  }, [handleOnEditClick]);

  return (
    <>
      <StyledTotalProgressTextWrapper __css={styles.totalProgressTextWrapper}>
        <StyledTotalGiftCountWrapper __css={styles.totalGiftCountWrapper}>
          <TextV2 fontSize={26} typographyVariant="hed1" color="mono14" marginRight={1}>
            {getCompletionText()}
          </TextV2>
          <IconButton minWidth={pxToRem(22)} height={pxToRem(22)} color="mono14" variant="ghost" aria-label={registryTexts.ariaEditLabel()} onClick={handleEditClick}>
            <EditIcon />
          </IconButton>
        </StyledTotalGiftCountWrapper>
      </StyledTotalProgressTextWrapper>
      <ProgressBar
        progressPercentage={completionPercent}
        height={16}
        gradientFill="linear-gradient(90deg, color(display-p3 0.337255 0 .95), color(display-p3 0.286275 0.317647 0.937255))"
      />
      <Flex marginTop={5} justifyContent="space-between">
        <TextV2 color="mono12" typographyVariant="label2">
          {subtitle}
        </TextV2>
        <TextV2 color="mono12" typographyVariant="label2">{`${formattedCompletionPercent}%`}</TextV2>
      </Flex>
    </>
  );
};
